import React, { useMemo } from 'react';
import { Icon, Modal, Pills, Text, Tooltip } from 'barte-design-system';
import { Check } from '@phosphor-icons/react';
import { BASE_ROLE, ROLE_ICON, ROLES, type Role, type User } from './roles';

interface ModalMatrizRolesProps {
  isOpen: boolean;
  onClose: () => void;
  users: User[];
}

export function ModalMatrizRoles({ isOpen, onClose, users }: ModalMatrizRolesProps) {
  // Ordem: Admin, Visualização (base) e os demais — 9 perfis = grade 3×3
  const admin = ROLES.find((r) => r.id === 'admin');
  const others = ROLES.filter((r) => r.id !== 'admin');
  const ordered = [admin, BASE_ROLE, ...others].filter(Boolean) as Role[];

  const cards = useMemo(
    () =>
      ordered.map((role) => {
        const matched = users.filter((u) => u.roleIds.includes(role.id));
        return { role, count: matched.length, emails: matched.map((u) => u.email) };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users],
  );

  return (
    <Modal isOpen={isOpen} title="Perfis" onClose={onClose} size="lg" className="up-perfis-modal">
      <div className="up-perfis-intro">
        <Text variant="body-sm">Cada perfil concede o conjunto de permissões abaixo.</Text>
      </div>

      <div className="up-perfis-grid">
        {cards.map(({ role, count, emails }) => {
          const countPill = (
            <span className="up-perfis-count">
              <Pills label={`${count}`} state="default" variant="light" size="sm" />
            </span>
          );
          return (
            <div key={role.id} className="up-perfis-card">
              <div className="up-perfis-card-head">
                <span className="up-perfis-card-name">
                  <Pills
                    label={role.name}
                    state="accent"
                    variant={role.id === 'admin' ? 'fill' : 'light'}
                    size="base"
                    icon={ROLE_ICON[role.id]}
                  />
                </span>
                {emails.length > 0 ? (
                  <Tooltip
                    placement="left"
                    title={`${count} usuário${count === 1 ? '' : 's'}`}
                    label={
                      <ul className="up-perm-tooltip">
                        {emails.map((e) => (
                          <li key={e}>{e}</li>
                        ))}
                      </ul>
                    }
                  >
                    {countPill}
                  </Tooltip>
                ) : (
                  countPill
                )}
              </div>

              <ul className="up-perfis-perm">
                {(role.id === 'admin' ? [role.description] : role.permissions).map((p) => (
                  <li key={p}>
                    <Icon icon={Check} size={14} weight="bold" />
                    <Text variant="body-xs">{p}</Text>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
